import { Injectable } from '@angular/core';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { ContractField } from '@app/models/firebase/contract';
import { ContractFieldType } from '@app/enums/contract-field-type.enum';
import { PdfCoordinateService } from './pdf-coordinate.service';

const PDF_RENDER = {
  FONT_SIZE_MIN: 7,
  FONT_SIZE_MAX: 11,
  FONT_SIZE_HEIGHT_RATIO: 0.5,
  HEADER_FONT_SIZE_BONUS: 2,
  TEXT_PADDING_X: 2,
  TEXT_PADDING_CLIP: 4,
  TEXT_Y_OFFSET: 3,
  DEFAULT_FIELD_WIDTH: 150,
  DEFAULT_FIELD_HEIGHT: 30,
  PAGES_PER_UNIT: 100,
  CHECKBOX_SIZE_RATIO: 0.7,
  CHECKBOX_CHECK_START_X: 0.2,
  CHECKBOX_CHECK_MID_X: 0.45,
  CHECKBOX_CHECK_END_X: 0.85,
  CHECKBOX_CHECK_LOW_Y: 0.25,
  CHECKBOX_CHECK_MID_Y: 0.5,
  CHECKBOX_CHECK_HIGH_Y: 0.75,
  CHECKBOX_STROKE_THICKNESS: 2,
  RADIO_SIZE_RATIO: 0.5,
} as const;

@Injectable({ providedIn: 'root' })
export class ContractPdfRendererService {

  constructor(private coordinateService: PdfCoordinateService) {}

  async renderWithFields(pdfUrl: string, allFields: ContractField[]): Promise<Uint8Array> {
    if (!pdfUrl) { 
      throw new Error('ContractPdfRendererService: pdfUrl no puede estar vacío.'); 
    }
    if (!Array.isArray(allFields)) { 
      throw new Error('ContractPdfRendererService: allFields debe ser un array.'); 
    }

    let arrayBuffer: ArrayBuffer;
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      arrayBuffer = await response.arrayBuffer();
    } catch (e) {
      throw new Error(`ContractPdfRendererService: no se pudo obtener el PDF desde "${pdfUrl}". ${e}`);
    }

    let pdfDoc: PDFDocument;
    try {
      pdfDoc = await PDFDocument.load(arrayBuffer);
    } catch (e) {
      throw new Error(`ContractPdfRendererService: el archivo PDF no es válido o está corrupto. ${e}`);
    }

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const pages = pdfDoc.getPages();
    const numPages = pages.length;

    for (const field of allFields) {
      if (!field?.id || !field?.type) { 
        continue; 
      }
      if (!this.isFieldVisible(field, allFields)) { 
        continue; 
      }

      const yRaw       = field.y || 0;
      const xPct       = field.x || 0;
      const pageIndex  = Math.min(Math.floor(yRaw / PDF_RENDER.PAGES_PER_UNIT), numPages - 1);
      const yPctInPage = yRaw % PDF_RENDER.PAGES_PER_UNIT;
      const page = pages[pageIndex];
      if (!page) { 
        continue; 
      }

      const { width: pageW, height: pageH } = page.getSize();
      const coords = this.coordinateService.relativeToPdfLib(
        {
          id: field.id,
          type: field.type,
          pageNumber: pageIndex,
          xRel: xPct / PDF_RENDER.PAGES_PER_UNIT,
          yRel: yPctInPage / PDF_RENDER.PAGES_PER_UNIT,
          widthRel: ((field.wPct || 0) / PDF_RENDER.PAGES_PER_UNIT) || ((field.width || PDF_RENDER.DEFAULT_FIELD_WIDTH) / pageW),
          heightRel: ((field.hPct || 0) / PDF_RENDER.PAGES_PER_UNIT) || ((field.height || PDF_RENDER.DEFAULT_FIELD_HEIGHT) / pageH),
        },
        pageW,
        pageH,
      );

      const { x, y: pureY, width: w, height: h } = coords;
      const fontSize = Math.max(PDF_RENDER.FONT_SIZE_MIN, Math.min(PDF_RENDER.FONT_SIZE_MAX, h * PDF_RENDER.FONT_SIZE_HEIGHT_RATIO));
      const textX = x + PDF_RENDER.TEXT_PADDING_X;
      const textY = pureY - PDF_RENDER.TEXT_Y_OFFSET + (h - fontSize) / 2;

      try {
        switch (field.type) {
          case ContractFieldType.TEXTAREA: {
            if (field.value == null || field.value === '') { break; }
            page.drawText(this.clipText(String(field.value), font, fontSize, w - PDF_RENDER.TEXT_PADDING_CLIP), { x: textX, y: textY, font, size: fontSize, color: rgb(0, 0, 0) });
            break;
          }
          case ContractFieldType.HEADERS: {
            const label = field.value ? String(field.value) : (field.name || '');
            if (!label) { break; }
            const headerSize = fontSize + PDF_RENDER.HEADER_FONT_SIZE_BONUS;
            page.drawText(this.clipText(label, fontBold, headerSize, w - PDF_RENDER.TEXT_PADDING_CLIP), { x: textX, y: textY, font: fontBold, size: headerSize, color: rgb(0, 0, 0) });
            break;
          }
          case ContractFieldType.IMAGE:
          case ContractFieldType.SIGNATURE:
          case ContractFieldType.INITIALS: {
            if (!field.value) { break; }
            try { await this.drawFieldImage(pdfDoc, page, field.value, x, pureY, w, h); } catch (e) { console.error(`Error al insertar imagen en campo ${field.id}:`, e); }
            break;
          }
          case ContractFieldType.CHECKBOX: {
            const checked = field.optionValue ? field.value === field.optionValue : !!field.value;
            if (checked) {
              const cbSize = Math.min(w, h) * PDF_RENDER.CHECKBOX_SIZE_RATIO;
              const cbX = x + (w - cbSize) / 2;
              const cbY = pureY + (h - cbSize) / 2;
              page.drawLine({ start: { x: cbX + cbSize * PDF_RENDER.CHECKBOX_CHECK_START_X, y: cbY + cbSize * PDF_RENDER.CHECKBOX_CHECK_MID_Y }, end: { x: cbX + cbSize * PDF_RENDER.CHECKBOX_CHECK_MID_X, y: cbY + cbSize * PDF_RENDER.CHECKBOX_CHECK_LOW_Y }, thickness: PDF_RENDER.CHECKBOX_STROKE_THICKNESS, color: rgb(0, 0, 0) });
              page.drawLine({ start: { x: cbX + cbSize * PDF_RENDER.CHECKBOX_CHECK_MID_X, y: cbY + cbSize * PDF_RENDER.CHECKBOX_CHECK_LOW_Y }, end: { x: cbX + cbSize * PDF_RENDER.CHECKBOX_CHECK_END_X, y: cbY + cbSize * PDF_RENDER.CHECKBOX_CHECK_HIGH_Y }, thickness: PDF_RENDER.CHECKBOX_STROKE_THICKNESS, color: rgb(0, 0, 0) });
            }
            break;
          }
          case ContractFieldType.RADIO: {
            const checked = field.optionValue ? field.value === field.optionValue : !!field.value;
            if (checked) {
              const rSize = Math.min(w, h) * PDF_RENDER.RADIO_SIZE_RATIO;
              page.drawEllipse({ x: x + w / 2, y: pureY + h / 2, xScale: rSize / 2, yScale: rSize / 2, color: rgb(0, 0, 0) });
            }
            break;
          }
          case ContractFieldType.FILE: {
            break;
          }
          case ContractFieldType.TEXT:
          case ContractFieldType.EMAIL:
          case ContractFieldType.PHONE:
          case ContractFieldType.NUMBER:
          case ContractFieldType.DATE:
          case ContractFieldType.DROPDOWN: {
            if (field.value == null || field.value === '') { break; }
            page.drawText(this.clipText(String(field.value), font, fontSize, w - PDF_RENDER.TEXT_PADDING_CLIP), { x: textX, y: textY, font, size: fontSize, color: rgb(0, 0, 0) });
            break;
          }
        }
      } catch (e) {
        console.error(`ContractPdfRendererService: error al renderizar el campo "${field.id}" (tipo: ${field.type}):`, e);
      }
    }

    return pdfDoc.save();
  }

  isFieldVisible(field: ContractField, allFields: ContractField[]): boolean {
    if (!field.conditionalLogic?.dependsOnFieldId) { return true; }

    const parentField = allFields.find(f => f.id === field.conditionalLogic.dependsOnFieldId);
    if (!parentField) { return true; }

    let effectiveValue: string;

    if (parentField.groupKey) {
      const groupFields = allFields.filter(f => f.groupKey === parentField.groupKey);
      if (parentField.type === ContractFieldType.RADIO) {
        effectiveValue = groupFields.find(f => !!f.value)?.value ?? '';
      } else if (parentField.type === ContractFieldType.CHECKBOX) {
        effectiveValue = groupFields.filter(f => f.value && f.value === f.optionValue).map(f => f.optionValue).join(',');
      } else {
        effectiveValue = parentField.value ?? '';
      }
    } else {
      effectiveValue = parentField.value ?? '';
    }

    const val    = effectiveValue.toString().toLowerCase();
    const target = (field.conditionalLogic.value ?? '').toString().toLowerCase();

    switch (field.conditionalLogic.operator) {
      case 'equals':     return val === target;
      case 'notEquals':  return val !== target;
      case 'includes':   return val.includes(target);
      case 'startsWith': return val.startsWith(target);
      case 'endsWith':   return val.endsWith(target);
      default:           return true;
    }
  }

  private async drawFieldImage(pdfDoc: PDFDocument, page: any, value: string, x: number, y: number, w: number, h: number): Promise<void> {
    let bytes: Uint8Array;

    if (value.startsWith('data:image')) {
      const base64 = value.split(',')[1];
      bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    } else if (value.startsWith('http')) {
      const imgResponse = await fetch(value);
      if (!imgResponse.ok) { throw new Error(`Failed to fetch image: ${imgResponse.statusText}`); }
      bytes = new Uint8Array(await imgResponse.arrayBuffer());
    } else {
      return;
    }

    const imageObj = this.isPng(bytes) ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
    page.drawImage(imageObj, { x, y, width: w, height: h });
  }

  private isPng(bytes: Uint8Array): boolean {
    return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  }

  private clipText(text: string, font: any, fontSize: number, maxWidth: number): string {
    let clipped = text;
    while (clipped.length > 0 && font.widthOfTextAtSize(clipped, fontSize) > maxWidth) {
      clipped = clipped.slice(0, -1);
    }
    return clipped;
  }
}
